import React from 'react';
import { Tooltip } from 'antd';
import { CheckCircleOutlined, EyeInvisibleOutlined, InfoCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

/**
 * Small building blocks of the ZK card screens. Presentational only: they
 * never decide who may see a value — callers pass what the backend returned
 * and the role flags.
 */

export type HiddenReason = 'salePrices' | 'costs' | 'margin' | 'adminOnly' | 'warehouse';

/**
 * „ukryte” instead of a value the role does not see (makieta `mask()`). A
 * cost hidden from the Handlowiec reads „koszt widzi administrator”, so it is
 * never mistaken for an unknown cost („koszt nieznany”).
 */
export function HiddenValue({ reason }: { reason: HiddenReason }) {
  const { t } = useTranslation(['orders']);
  return (
    <Tooltip title={t(`orders:process.hidden.${reason}`)}>
      <span className="zk-mask" data-testid="zk-hidden-value" aria-label={t(`orders:process.hidden.${reason}`)}>
        <EyeInvisibleOutlined aria-hidden="true" />
        {reason === 'costs' ? t('orders:process.hidden.costsLabel') : t('orders:process.hidden.label')}
      </span>
    </Tooltip>
  );
}

/**
 * A line value whose per-line preview has not answered yet (after a save or a status change).
 * Neutral „liczę…”, never „—”, so the line does not contradict the tiles that already show totals.
 */
export function LineValuePending() {
  const { t } = useTranslation(['orders']);
  return <span className="zk-num zk-pending" data-testid="zk-line-pending">{t('orders:process.margin.lineLoading')}</span>;
}

/** The one note shown when the per-line preview failed (instead of unexplained „—” per line). */
export function LineValuesFailedNote() {
  const { t } = useTranslation(['orders']);
  return (
    <div className="zk-callout zk-callout--muted zk-callout--flush" role="alert" data-testid="zk-line-costs-failed">
      {t('orders:process.margin.linesFailed')}
    </div>
  );
}

export function StepSection({
  title,
  icon,
  aside,
  hint,
  children,
  testId,
}: {
  title: React.ReactNode;
  icon?: React.ReactNode;
  aside?: React.ReactNode;
  hint?: React.ReactNode;
  children?: React.ReactNode;
  testId?: string;
}) {
  return (
    <section className="zk-section" data-testid={testId}>
      <div className="zk-section__header">
        <h3 className="zk-section__title">{icon}{icon ? ' ' : null}{title}</h3>
        {aside && <div className="zk-section__aside">{aside}</div>}
      </div>
      {hint && <p className="zk-section__hint">{hint}</p>}
      {children}
    </section>
  );
}

export function KeyValueList({ rows, single = false }: { rows: Array<[React.ReactNode, React.ReactNode]>; single?: boolean }) {
  return (
    <dl className={`zk-kv${single ? ' zk-kv--single' : ''}`}>
      {rows.map(([label, value], index) => (
        // Labels are static copy; the index keeps duplicates (none expected) stable.
        // eslint-disable-next-line react/no-array-index-key
        <div className="zk-kv__row" key={index}>
          <dt>{label}</dt>
          <dd>{value ?? '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

export function InfoList({ rows }: { rows: Array<[React.ReactNode, React.ReactNode]> }) {
  return (
    <div className="zk-list">
      {rows.map(([label, value], index) => (
        // eslint-disable-next-line react/no-array-index-key
        <div className="zk-list__row" key={index}>
          <span>{label}</span>
          <span>{value ?? '—'}</span>
        </div>
      ))}
    </div>
  );
}

export function Callout({
  tone = 'info',
  flush = false,
  children,
  testId,
}: {
  tone?: 'info' | 'success' | 'warning' | 'muted';
  flush?: boolean;
  children: React.ReactNode;
  testId?: string;
}) {
  const icon = tone === 'success' ? <CheckCircleOutlined /> : tone === 'warning' ? <WarningOutlined /> : <InfoCircleOutlined />;
  return (
    <div className={`zk-callout zk-callout--${tone}${flush ? ' zk-callout--flush' : ''}`} role="status" data-testid={testId}>
      <span aria-hidden="true">{icon}</span>
      <span>{children}</span>
    </div>
  );
}
